/**
 * Admin → Learn Diagnostics
 * Read-only audit surface for the Learn library. Surfaces missing videos,
 * duplicate embeds, missing thumbnail overrides, missing metadata, and
 * a per-category/difficulty/tier count.
 *
 * Powered by GET /api/admin/learn/diagnostics (no writes from this page —
 * the actual fix actions live on /admin/maintenance).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, AlertTriangle, Loader2, Sparkles, VideoOff,
  Copy as CopyIcon, ImageOff, BarChart3, ArrowRight,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLearn() {
  const [dx, setDx] = useState(null);
  const [err, setErr] = useState(null);
  const token = localStorage.getItem('carpcast_token');

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/admin/learn/diagnostics`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!cancelled) setDx(r.data); })
      .catch((e) => { if (!cancelled) setErr(e.response?.data?.detail || 'Failed to load'); });
    return () => { cancelled = true; };
  }, [token]);

  if (err) return <div className="min-h-screen p-8 text-red-400" data-testid="admin-learn-error">{err}</div>;
  if (!dx) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-[#c8b273] animate-spin" />
    </div>
  );

  const healthy = dx.health === 'healthy';
  const HealthIcon = healthy ? CheckCircle2 : AlertTriangle;

  return (
    <div className="min-h-screen px-6 py-10" data-testid="admin-learn">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <p className="data-label text-[#c8b273] mb-1">Admin · Learn diagnostics</p>
            <h1 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-extrabold uppercase tracking-wider text-white">
              Learn Library Health
            </h1>
          </div>
          <Link
            to="/admin/maintenance"
            className="inline-flex items-center gap-1.5 text-sm text-[#c8b273] hover:underline"
            data-testid="admin-learn-maintenance-link"
          >
            Go to Maintenance <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Top status banner */}
        <div className={`glass-card p-5 mb-8 flex items-start gap-3 ${
          healthy ? 'border-green-500/30' : 'border-orange-500/40'
        }`} data-testid="admin-learn-health">
          <HealthIcon className={`w-6 h-6 mt-0.5 ${healthy ? 'text-green-400' : 'text-orange-400'}`} />
          <div>
            <p className={`font-bold uppercase tracking-wider text-sm ${healthy ? 'text-green-400' : 'text-orange-400'}`}>
              {healthy ? 'Library healthy' : 'Needs attention'}
            </p>
            <p className="text-[#c0c4c0] text-sm mt-0.5">
              {dx.playable_count} / {dx.total} lessons currently playable.
            </p>
          </div>
        </div>

        {/* Top stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <Stat label="Content mismatches" value={dx.content_mismatch_count || 0} testid="dx-content-mismatch" tone={dx.content_mismatch_count ? 'red' : 'green'} />
          <Stat label="Untracked lessons" value={dx.untracked_count || 0} testid="dx-untracked" tone={dx.untracked_count ? 'orange' : 'green'} />
          <Stat label="Total" value={dx.total} testid="dx-total" />
          <Stat label="Playable" value={dx.playable_count} testid="dx-playable" tone="green" />
          <Stat label="Missing video" value={dx.missing_video_count} testid="dx-missing-video" tone={dx.missing_video_count ? 'red' : 'green'} />
          <Stat label="Dup embeds" value={dx.duplicate_video_groups} testid="dx-duplicates" tone={dx.duplicate_video_groups ? 'red' : 'green'} />
          <Stat label="Bad URL shape" value={dx.bad_url_shape_count} testid="dx-bad-url" tone={dx.bad_url_shape_count ? 'red' : 'green'} />
          <Stat label="Missing metadata" value={dx.missing_metadata_count} testid="dx-missing-meta" tone={dx.missing_metadata_count ? 'orange' : 'green'} />
          <Stat label="Thumbnail overrides" value={(dx.total - dx.auto_thumbnail_count)} testid="dx-thumb-override" />
          <Stat label="Auto thumbnails" value={dx.auto_thumbnail_count} testid="dx-thumb-auto" tone="muted" />
        </div>

        {/* Breakdown grid */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <BreakdownCard title="By category" data={dx.by_category} icon={BarChart3} testid="dx-by-cat" />
          <BreakdownCard title="By difficulty" data={dx.by_difficulty} icon={Sparkles} testid="dx-by-diff" />
          <BreakdownCard title="By tier" data={dx.by_tier} icon={Sparkles} testid="dx-by-tier" />
        </div>

        {/* Detail sections — only render when there's actually a problem */}
        {dx.details?.missing_video?.length > 0 && (
          <DetailList title="Lessons missing video" icon={VideoOff} rows={dx.details.missing_video} testid="dx-missing-list" />
        )}
        {dx.details?.bad_url_shape?.length > 0 && (
          <DetailList title="Bad URL shape" icon={AlertTriangle} rows={dx.details.bad_url_shape} testid="dx-bad-list" />
        )}
        {dx.details?.duplicates?.length > 0 && (
          <DuplicateList rows={dx.details.duplicates} />
        )}
        {dx.details?.missing_metadata?.length > 0 && (
          <MetaList rows={dx.details.missing_metadata} />
        )}

        {dx.details?.content_mismatches?.length > 0 && (
          <div className="glass-card p-5 mb-4" data-testid="dx-mismatch-list">
            <h3 className="flex items-center gap-2 font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wider text-white mb-3">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Content mismatches <span className="text-red-400">({dx.details.content_mismatches.length})</span>
            </h3>
            <ul className="space-y-2 text-sm">
              {dx.details.content_mismatches.map((m) => (
                <li key={m.id} className="bg-[#141714] border border-red-500/30 rounded px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-white truncate">
                      <span className="text-[#6b7d6b] font-mono mr-2">{m.id}</span>
                      {m.title}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-[#c8b273]">{m.source}</span>
                  </div>
                  <div className="mt-1 text-xs text-[#8a948a] space-y-0.5">
                    {m.url_drift && <p>URL drift — actual <code className="text-red-300">{m.actual_video?.split('/embed/')[1] || '(none)'}</code> ≠ expected <code className="text-green-300">{m.expected_video.split('/embed/')[1]}</code></p>}
                    {m.title_drift && <p>Title doesn't contain expected keyword(s): <code className="text-orange-300">{m.expected_keywords.join(', ')}</code></p>}
                  </div>
                </li>
              ))}
            </ul>
            <p className="text-xs text-[#8a948a] mt-3">Add these IDs to <code className="text-[#c8b273]">FORCE_OVERWRITE</code> in <code>learn_content_map.py</code> then run the backfill.</p>
          </div>
        )}

        {dx.details?.untracked?.length > 0 && (
          <div className="glass-card p-5 mb-4" data-testid="dx-untracked-list">
            <h3 className="flex items-center gap-2 font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wider text-white mb-3">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
              Untracked lessons <span className="text-orange-400">({dx.details.untracked.length})</span>
            </h3>
            <p className="text-xs text-[#8a948a] mb-2">No entry in <code className="text-[#c8b273]">learn_content_map.LESSON_CONTENT</code> — these will not be audited for title/video drift until added.</p>
            <ul className="space-y-2 text-sm">
              {dx.details.untracked.map((r) => (
                <li key={r.id} className="bg-[#141714] border border-[#1f221f] rounded px-3 py-2 flex items-center justify-between gap-2">
                  <span className="text-white truncate">
                    <span className="text-[#6b7d6b] font-mono mr-2">{r.id}</span>
                    {r.title}
                  </span>
                  <span className="text-[#8a948a] text-xs capitalize">{r.category}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Always-on info block */}
        <div className="glass-card p-5 mt-2 text-sm text-[#8a948a]" data-testid="admin-learn-runbook">
          <p className="data-label text-[#c8b273] mb-2">Run-book</p>
          <ul className="space-y-1.5 list-disc list-inside">
            <li>Need to fix missing/wrong videos? → <Link to="/admin/maintenance" className="text-[#c8b273] hover:underline">/admin/maintenance</Link> → "Backfill Learn tutorial videos".</li>
            <li>Duplicate video IDs require adding the lesson to <code className="text-[#c8b273]">DEDUP_OVERWRITES</code> in <code>routes/admin_cleanup.py</code>.</li>
            <li>The frontend already shows a "Watch on YouTube" fallback if a runtime iframe load fails — this audit is for the static data shape only, not for real-time delivery health.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, tone = 'default', testid }) => {
  const colour = ({
    default: 'text-white',
    green: 'text-green-400',
    orange: 'text-orange-400',
    red: 'text-red-400',
    muted: 'text-[#8a948a]',
  })[tone] || 'text-white';
  return (
    <div className="bg-[#0b0d0b] border border-white/10 rounded-lg p-3" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#8a948a]">{label}</p>
      <p className={`font-['Barlow_Condensed'] text-3xl font-extrabold ${colour}`}>{value}</p>
    </div>
  );
};

const BreakdownCard = ({ title, data, icon: Icon, testid }) => (
  <div className="bg-[#0b0d0b] border border-white/10 rounded-lg p-4" data-testid={testid}>
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-[#c8b273]" />
      <h3 className="font-bold uppercase tracking-wider text-sm text-white">{title}</h3>
    </div>
    <div className="space-y-1.5">
      {Object.entries(data || {}).map(([k, v]) => (
        <div key={k} className="flex items-center justify-between text-sm">
          <span className="text-[#c0c4c0] capitalize">{k || '—'}</span>
          <span className="font-bold text-[#c8b273]">{v}</span>
        </div>
      ))}
    </div>
  </div>
);

const DetailList = ({ title, icon: Icon, rows, testid }) => (
  <div className="glass-card p-5 mb-4" data-testid={testid}>
    <h3 className="flex items-center gap-2 font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wider text-white mb-3">
      <Icon className="w-5 h-5 text-orange-400" />
      {title} <span className="text-orange-400">({rows.length})</span>
    </h3>
    <ul className="space-y-2 text-sm">
      {rows.map((r) => (
        <li key={r.id} className="bg-[#141714] border border-[#1f221f] rounded px-3 py-2 flex items-center justify-between gap-2">
          <span className="text-white truncate">
            <span className="text-[#6b7d6b] font-mono mr-2">{r.id}</span>
            {r.title}
          </span>
          <span className="text-[#8a948a] text-xs capitalize">{r.category}</span>
        </li>
      ))}
    </ul>
  </div>
);

const DuplicateList = ({ rows }) => (
  <div className="glass-card p-5 mb-4" data-testid="dx-dup-list">
    <h3 className="flex items-center gap-2 font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wider text-white mb-3">
      <CopyIcon className="w-5 h-5 text-red-400" />
      Duplicate video embeds <span className="text-red-400">({rows.length} group{rows.length === 1 ? '' : 's'})</span>
    </h3>
    <div className="space-y-3">
      {rows.map((g) => (
        <div key={g.video_url} className="bg-[#141714] border border-red-500/30 rounded p-3">
          <p className="text-xs font-mono text-red-300 truncate mb-2">{g.video_url}</p>
          <ul className="space-y-1 pl-3">
            {g.lessons.map((l) => (
              <li key={l.id} className="text-sm text-[#c0c4c0]">
                <span className="text-[#6b7d6b] font-mono mr-2">{l.id}</span>
                {l.title}
                <span className="text-[#8a948a] text-xs ml-2 capitalize">· {l.category}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </div>
);

const MetaList = ({ rows }) => (
  <div className="glass-card p-5 mb-4" data-testid="dx-meta-list">
    <h3 className="flex items-center gap-2 font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wider text-white mb-3">
      <ImageOff className="w-5 h-5 text-orange-400" />
      Lessons with metadata gaps <span className="text-orange-400">({rows.length})</span>
    </h3>
    <ul className="space-y-2 text-sm">
      {rows.map((r) => (
        <li key={r.id} className="bg-[#141714] border border-[#1f221f] rounded px-3 py-2 flex items-center justify-between gap-2">
          <span className="text-white truncate">
            <span className="text-[#6b7d6b] font-mono mr-2">{r.id}</span>
            {r.title || '<no title>'}
          </span>
          <span className="text-orange-300 text-xs">{r.missing.join(', ')}</span>
        </li>
      ))}
    </ul>
  </div>
);
